/**
 * calcoloPremi.ts
 * ================
 * Utility per il calcolo dei premi insegna.
 *
 * STATO ATTUALE (fase 1):
 * Il fatturato viene inserito manualmente dall'utente nella pagina PremiInsegna.
 * Il calcolo è puramente simulativo e non viene mai salvato.
 *
 * INTEGRAZIONE FUTURA CON CICLO ATTIVO:
 * Quando il modulo "Ciclo Attivo" sarà sviluppato, il fatturato non sarà più
 * inserito manualmente ma verrà calcolato automaticamente come somma delle
 * fatture attive dell'insegna per l'anno selezionato.
 * Il punto di integrazione è la funzione `calcolaPremiInsegna`:
 * basterà sostituire il parametro `fatturatoManuale` con il risultato della
 * chiamata API GET /api/ciclo-attivo/fatturato-insegna?insegnaId=X&anno=Y
 *
 * I metodi di calcolo supportati sono due (configurabili per ogni premio):
 *
 * PROGRESSIVO: la percentuale si applica solo alla quota di fatturato
 * che ricade in ogni scaglione, come il calcolo IRPEF.
 * Esempio: fatturato 150.000€, scaglioni 2% fino a 100k e 3% oltre
 *   → scaglione 1: 100.000 × 2% = 2.000€
 *   → scaglione 2:  50.000 × 3% = 1.500€
 *   → totale: 3.500€
 *
 * FLAT: si individua lo scaglione in cui ricade il fatturato totale
 * e si applica la sua percentuale all'intero fatturato.
 * Esempio: stesso fatturato 150.000€, stessi scaglioni
 *   → scaglione raggiunto: oltre 100k → 3%
 *   → totale: 150.000 × 3% = 4.500€
 */

interface Scaglione {
  scaglioneMin: string;
  scaglioneMax: string | null;
  percentuale?: string;
  importoFisso?: string;
}

interface Premio {
  id: number;
  tipo: string;
  descrizione: string;
  metodoCalcolo: string;
  scaglioni: Scaglione[];
}

interface DettaglioScaglione {
  scaglioneMin: number;
  scaglioneMax: number | null;
  quotaFatturato: number;
  percentuale: number | null;
  importoFisso: number | null;
  importoCalcolato: number;
}

interface CalcoloSingoloPremio {
  totale: number;
  dettaglioScaglioni: DettaglioScaglione[];
  metodoCalcolo: string;
}

interface RisultatoPremio {
  id: number;
  tipo: string;
  descrizione: string;
  metodoCalcolo: string;
  totale: number;
  dettaglioScaglioni: DettaglioScaglione[];
}

interface CalcoloPremiInsegna {
  premi: RisultatoPremio[];
  totaleGenerale: number;
}

/**
 * Calcola il premio per un singolo PremioInsegna dato un fatturato.
 *
 * @param premio - Record PremioInsegna con array scaglioni incluso
 * @param fatturato - Fatturato su cui calcolare il premio
 *                             FASE 1: inserito manualmente dall'utente
 *                             FASE 2 (ciclo attivo): verrà passato dalla chiamata
 *                             GET /api/ciclo-attivo/fatturato-insegna
 * @returns { totale, dettaglioScaglioni }
 */
function calcolaSingoloPremio(premio: Premio, fatturato: number): CalcoloSingoloPremio {
  if (!premio.scaglioni || premio.scaglioni.length === 0) {
    return { totale: 0, dettaglioScaglioni: [], metodoCalcolo: premio.metodoCalcolo };
  }

  const scaglioni = [...premio.scaglioni].sort((a, b) => parseFloat(a.scaglioneMin) - parseFloat(b.scaglioneMin));
  const dettaglioScaglioni: DettaglioScaglione[] = [];
  let totale = 0;

  if (premio.metodoCalcolo === 'PROGRESSIVO') {
    // Metodo progressivo: calcola per ogni scaglione la quota di fatturato che ricade in esso
    for (let i = 0; i < scaglioni.length; i++) {
      const scaglione = scaglioni[i];
      const min = parseFloat(scaglione.scaglioneMin);
      const max = scaglione.scaglioneMax ? parseFloat(scaglione.scaglioneMax) : null;

      // Se il fatturato non raggiunge questo scaglione, fermiamoci
      if (fatturato < min) {
        break;
      }

      // Calcola quanto fatturato ricade in questo scaglione
      let quotaScaglione: number;
      if (max === null) {
        // Ultimo scaglione (illimitato)
        quotaScaglione = Math.max(0, fatturato - min);
      } else {
        // Scaglione con limite superiore
        quotaScaglione = Math.min(fatturato, max) - min;
      }

      if (quotaScaglione <= 0) {
        continue;
      }

      // Calcola il premio per questo scaglione
      let importoScaglione: number;
      if (scaglione.percentuale) {
        const perc = parseFloat(scaglione.percentuale);
        importoScaglione = (quotaScaglione * perc) / 100;
      } else if (scaglione.importoFisso) {
        importoScaglione = parseFloat(scaglione.importoFisso);
      } else {
        importoScaglione = 0;
      }

      totale += importoScaglione;

      dettaglioScaglioni.push({
        scaglioneMin: min,
        scaglioneMax: max,
        quotaFatturato: quotaScaglione,
        percentuale: scaglione.percentuale ? parseFloat(scaglione.percentuale) : null,
        importoFisso: scaglione.importoFisso ? parseFloat(scaglione.importoFisso) : null,
        importoCalcolato: importoScaglione,
      });
    }
  } else if (premio.metodoCalcolo === 'FLAT') {
    // Metodo flat: trova lo scaglione in cui ricade il fatturato e applica la sua % all'intero fatturato
    let scaglioneApplicabile: Scaglione | null = null;

    for (const scaglione of scaglioni) {
      const min = parseFloat(scaglione.scaglioneMin);
      const max = scaglione.scaglioneMax ? parseFloat(scaglione.scaglioneMax) : null;

      if (max === null) {
        // Scaglione illimitato: se il fatturato è >= min, questo è lo scaglione
        if (fatturato >= min) {
          scaglioneApplicabile = scaglione;
        }
      } else {
        // Scaglione con limite: se il fatturato è nel range [min, max]
        if (fatturato >= min && fatturato <= max) {
          scaglioneApplicabile = scaglione;
          break;
        }
      }
    }

    if (scaglioneApplicabile) {
      if (scaglioneApplicabile.percentuale) {
        const perc = parseFloat(scaglioneApplicabile.percentuale);
        totale = (fatturato * perc) / 100;
      } else if (scaglioneApplicabile.importoFisso) {
        totale = parseFloat(scaglioneApplicabile.importoFisso);
      }

      dettaglioScaglioni.push({
        scaglioneMin: parseFloat(scaglioneApplicabile.scaglioneMin),
        scaglioneMax: scaglioneApplicabile.scaglioneMax ? parseFloat(scaglioneApplicabile.scaglioneMax) : null,
        quotaFatturato: fatturato,
        percentuale: scaglioneApplicabile.percentuale ? parseFloat(scaglioneApplicabile.percentuale) : null,
        importoFisso: scaglioneApplicabile.importoFisso ? parseFloat(scaglioneApplicabile.importoFisso) : null,
        importoCalcolato: totale,
      });
    }
  }

  return {
    totale,
    dettaglioScaglioni,
    metodoCalcolo: premio.metodoCalcolo,
  };
}

/**
 * Calcola tutti i premi di un'insegna per un dato fatturato.
 *
 * @param premi - Array di PremioInsegna (con scaglioni inclusi)
 * @param fatturato - Vedi nota su calcolaSingoloPremio
 * @returns { premi: [...], totaleGenerale }
 */
export function calcolaPremiInsegna(premi: Premio[], fatturato: number): CalcoloPremiInsegna {
  const risultati: RisultatoPremio[] = premi.map((premio) => {
    const calcolo = calcolaSingoloPremio(premio, fatturato);
    return {
      id: premio.id,
      tipo: premio.tipo,
      descrizione: premio.descrizione,
      metodoCalcolo: premio.metodoCalcolo,
      totale: calcolo.totale,
      dettaglioScaglioni: calcolo.dettaglioScaglioni,
    };
  });

  const totaleGenerale = risultati.reduce((sum, r) => sum + r.totale, 0);

  return {
    premi: risultati,
    totaleGenerale,
  };
}

export { calcolaSingoloPremio };
